import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BASE_MODEL_ID } from '../constants'
import {
  buildModelParamsPayload,
  isSameModelParamSelection,
  parseModelParams,
} from '../utils/modelCalculationParams'
import { getModelDetail, getModelParamDefinitions, updateModel } from '../services/modelService'
import type { ModelAddFormValues, ModelCalculationParamSelection, ModelDetailRecord, ModelParamDefinition } from '../types/modelParam'
import { showApiError, showError, showSuccess } from '@/lib/utils/notifications'
import { getModelListReactPath } from '@/lib/utils/modelNavigation'

interface BasicInfoSnapshot {
  model_name: string
  description: string
  is_active: 0 | 1
  params: ModelCalculationParamSelection
}

function to01(value: unknown): 0 | 1 {
  if (value === true) return 1
  if (value === false) return 0
  const n = Number(value)
  return n ? 1 : 0
}

export function useModelEdit(modelId: number) {
  const navigate = useNavigate()
  const [definitions, setDefinitions] = useState<ModelParamDefinition[]>([])
  const [modelData, setModelData] = useState<ModelDetailRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [validateForm, setValidateForm] = useState(false)
  const [form, setForm] = useState<ModelAddFormValues>({
    model_name: '',
    description: '',
    is_active: 1,
    calculationParams: {},
  })
  const [processorFile, setProcessorFile] = useState<File | null>(null)
  const [processorFileName, setProcessorFileName] = useState('')
  const [pklFile, setPklFile] = useState<File | null>(null)
  const [pklFileName, setPklFileName] = useState('')
  const originalRef = useRef<BasicInfoSnapshot | null>(null)

  const isBaseModel = modelId === BASE_MODEL_ID || modelData?.model_id === BASE_MODEL_ID

  const applyModelToForm = useCallback(
    (detail: ModelDetailRecord, defs: ModelParamDefinition[]) => {
      const paramSelection = parseModelParams(detail.params, defs)
      const nextForm: ModelAddFormValues = {
        model_name: detail.model_name ?? '',
        description: detail.description ?? '',
        is_active: to01(detail.is_active ?? 1),
        calculationParams: paramSelection,
      }
      setForm(nextForm)
      setProcessorFile(null)
      setProcessorFileName('')
      setPklFile(null)
      setPklFileName('')
      originalRef.current = {
        model_name: nextForm.model_name,
        description: nextForm.description,
        is_active: to01(nextForm.is_active),
        params: { ...paramSelection },
      }
    },
    [],
  )

  const loadModel = useCallback(async (defs: ModelParamDefinition[]) => {
    const detail = await getModelDetail(modelId)
    setModelData(detail)
    applyModelToForm(detail, defs)
  }, [applyModelToForm, modelId])

  useEffect(() => {
    if (!modelId || Number.isNaN(modelId)) {
      navigate(getModelListReactPath())
      return
    }

    let cancelled = false

    async function load() {
      setLoading(true)
      try {
        const defs = await getModelParamDefinitions()
        if (cancelled) return
        setDefinitions(defs)
        await loadModel(defs)
      } catch (error) {
        if (!cancelled) {
          showApiError(error)
          navigate(getModelListReactPath())
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [loadModel, modelId, navigate])

  const getSnapshot = useCallback(
    (current: ModelAddFormValues): BasicInfoSnapshot => ({
      model_name: current.model_name,
      description: current.description,
      is_active: to01(current.is_active),
      params: current.calculationParams,
    }),
    [],
  )

  const isDirty = useMemo(() => {
    const original = originalRef.current
    if (!original) return false
    const current = getSnapshot(form)
    return (
      current.model_name !== original.model_name ||
      current.description !== original.description ||
      (!isBaseModel && current.is_active !== original.is_active) ||
      !isSameModelParamSelection(current.params, original.params, definitions) ||
      !!processorFile ||
      !!pklFile
    )
  }, [definitions, form, getSnapshot, isBaseModel, pklFile, processorFile])

  const updateField = <K extends keyof Omit<ModelAddFormValues, 'calculationParams'>>(
    key: K,
    value: ModelAddFormValues[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const updateCalculationParams = (calculationParams: ModelCalculationParamSelection) => {
    setForm((prev) => ({ ...prev, calculationParams }))
  }

  const handleProcessorFile = (file: File | null) => {
    setProcessorFile(file)
    setProcessorFileName(file?.name ?? '')
  }

  const handlePklFile = (file: File | null) => {
    setPklFile(file)
    setPklFileName(file?.name ?? '')
  }

  const cancel = () => {
    navigate(getModelListReactPath())
  }

  const submit = async () => {
    if (!isDirty || submitting) {
      if (!isDirty) {
        showError('No changes to save.')
      }
      return
    }

    setValidateForm(true)

    const trimmedName = form.model_name.trim()
    if (!trimmedName) {
      showError('Model Name is required')
      return
    }

    setSubmitting(true)
    try {
      await updateModel(modelId, {
        model_name: trimmedName,
        description: form.description,
        is_active: isBaseModel ? 1 : to01(form.is_active),
        params: JSON.stringify(buildModelParamsPayload(form.calculationParams, definitions)),
        model_processor_file: processorFile ?? undefined,
        model_pkl_file: pklFile ?? undefined,
      })

      showSuccess('Model updated successfully!')
      await loadModel(definitions)
    } catch (error) {
      showApiError(error)
      if (modelData) {
        applyModelToForm(modelData, definitions)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const canTestPklFile = !!modelData?.model_processor_name

  return {
    definitions,
    modelData,
    loading,
    submitting,
    validateForm,
    form,
    processorFile,
    processorFileName,
    pklFile,
    pklFileName,
    isDirty,
    isBaseModel,
    canTestPklFile,
    updateField,
    updateCalculationParams,
    setProcessorFile: handleProcessorFile,
    setPklFile: handlePklFile,
    cancel,
    submit,
  }
}
